import React, { useCallback } from 'react';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';
import { DEFAULT_SAMS_ACCESS_CONFIG } from '../AreaAccessConfig/constants';

/** Default SAMS projection parameters. Owned by ResetSamsProjection. */
export const SAMS_DEFAULT_PARAMS = {
  inputDAS: 24.0,
  fleetSize: 300,
  nYears: 3,
  lpueMethod: 'dynamic' as const,
  accessConfig: DEFAULT_SAMS_ACCESS_CONFIG,
};

interface ResetSamsProjectionData {
  /** Override: called when user clicks Reset. If provided, replaces built-in logic. */
  onResetSams?: () => void;
  /** Disabled when updating/loading. */
  resetSamsDisabled?: boolean;
}

/**
 * Reset button for SAMS (scallop) projection.
 * Owns the default values; calls onResetSamsToDefaults when page provides it.
 */
export default function ResetSamsProjection(props: RegistryComponentProps): React.ReactElement {
  const pageData = usePageDataContextOptional();
  const data = getRegistryData<ResetSamsProjectionData>(props);
  const overrideReset = data.onResetSams;
  const pageResetSams = pageData?.actions?.resetSamsProjection as (() => void) | undefined;
  const onResetToDefaults = pageData?.actions?.onResetSamsToDefaults as ((params: typeof SAMS_DEFAULT_PARAMS) => void) | undefined;
  const scallopLoading = (pageData?.data?.scallopLoading as boolean | undefined) ?? false;
  const updatingPStar = (pageData?.data?.updatingPStar as boolean | undefined) ?? false;
  const disabled = (data.resetSamsDisabled ?? updatingPStar ?? scallopLoading) ?? false;

  const handleReset = useCallback(() => {
    if (typeof overrideReset === 'function') {
      overrideReset();
      return;
    }
    if (typeof onResetToDefaults === 'function') {
      onResetToDefaults(SAMS_DEFAULT_PARAMS);
      return;
    }
    if (typeof pageResetSams === 'function') {
      pageResetSams();
    }
  }, [overrideReset, onResetToDefaults, pageResetSams]);

  const handleClick = useCallback(() => handleReset(), [handleReset]);

  return (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
      <button
        className="btn noscroll-refresh-database-button"
        onClick={handleClick}
        disabled={disabled}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      >
        Reset to Default
      </button>
    </div>
  );
}
